import { Box, Typography } from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import toast, { Toaster } from 'react-hot-toast';
import { useLoginMutation } from "../../state/api";
import { setLogin } from "../../state";
import "./styles.css";

// Initial Values
const formInitialData = {
    email: "",
    password: "",
}

// Form validations
const validationSchema = yup.object().shape({
    email: yup.string().email("Invalid email").required("Email is required"),
    password: yup.string().required("Password is required"),
});

const LoginPage = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [showLoginForm, setShowLoginForm] = useState(false);

    // Correctly use the mutation hook at the top level
    const [login, { data: loggedInData, isLoading, isSuccess, isError, error }] = useLoginMutation(); 

    const handleFormSubmit = async (values, onSubmitProps) => {
        await login(values); // Call the trigger function
        onSubmitProps.resetForm();
    };

    // Use useEffect to react to mutation state changes
    useEffect(() => {
        let toastId;

        if (isLoading) {
            toastId = toast.loading('Logging in...');
        }

        if (isSuccess && loggedInData) {
            toast.success('Logged in Successfully', { id: toastId });
            dispatch(
                setLogin({
                    userId: loggedInData.userId,
                    token: loggedInData.token,
                })
            );
            navigate('/Dashboard');
        }

        if (isError) {
            const errorMessage = error?.data?.msg || "An unexpected error occurred.";
            toast.error(errorMessage, { id: toastId });
        }

        // Cleanup function for the toast
        return () => {
            if (toastId) {
                toast.dismiss(toastId);
            }
        };
    }, [isLoading, isSuccess, isError, loggedInData, error, dispatch, navigate]);

    // Show login form after delay (like in original)
    useEffect(() => {
        const timer = setTimeout(() => {
            setShowLoginForm(true);
        }, 4500);

        return () => clearTimeout(timer);
    }, []);

    return (
        <Box sx={{ backgroundColor: '#1f2029', color: '#ffeba7', minHeight: '100vh' }}>
            <Toaster position="top-right" />
            
            {/* Loading screen with company name */}
            <div className={`des ${showLoginForm ? 'hidden' : ''}`}>
                <span>Mckenzie</span>
            </div>

            {/* Login Form */}
            <div id="login-form" className="section" style={{ display: showLoginForm ? 'block' : 'none' }}>
                <div className="container">
                    <div className="row full-height justify-content-center">
                        <div className="col-12 text-center align-self-center py-5">
                            <div className="section pb-5 pt-5 pt-sm-2 text-center">
                                <h6 className="mb-0 pb-3">
                                    <span>Log In </span>
                                    <span>Sign Up</span>
                                </h6>
                                <input className="checkbox" type="checkbox" id="reg-log" name="reg-log"/>
                                <label htmlFor="reg-log"></label>
                                <div className="card-3d-wrap mx-auto">
                                    <div className="card-3d-wrapper">
                                        {/* Login Card Front */}
                                        <div className="card-front">
                                            <div className="center-wrap">
                                                <div className="section text-center">
                                                    <h4 className="mb-4 pb-3">Log In</h4>
                                                    
                                                    <Formik
                                                        onSubmit={(values, onSubmitProps) => handleFormSubmit(values, onSubmitProps)}
                                                        initialValues={formInitialData}
                                                        validationSchema={validationSchema}
                                                    >
                                                        {({
                                                            values,
                                                            errors,
                                                            touched,
                                                            handleBlur,
                                                            handleChange,
                                                            handleSubmit,
                                                        }) => (
                                                            <form onSubmit={handleSubmit}>
                                                                <div className="form-group">
                                                                    <input 
                                                                        type="email" 
                                                                        className="form-style" 
                                                                        placeholder="Email" 
                                                                        id="email"
                                                                        name="email"
                                                                        value={values.email}
                                                                        onChange={handleChange}
                                                                        onBlur={handleBlur}
                                                                        style={{
                                                                            borderColor: touched.email && errors.email ? '#ff6b6b' : 'transparent'
                                                                        }}
                                                                    />
                                                                    <i className="input-icon uil uil-at"></i>
                                                                    {touched.email && errors.email && (
                                                                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1, fontSize: '12px' }}>
                                                                            {errors.email}
                                                                        </Typography>
                                                                    )}
                                                                </div>
                                                                <div className="form-group mt-2">
                                                                    <input 
                                                                        type="password" 
                                                                        className="form-style" 
                                                                        placeholder="Password" 
                                                                        id="password"
                                                                        name="password"
                                                                        value={values.password}
                                                                        onChange={handleChange}
                                                                        onBlur={handleBlur}
                                                                        style={{
                                                                            borderColor: touched.password && errors.password ? '#ff6b6b' : 'transparent'
                                                                        }}
                                                                    />
                                                                    <i className="input-icon uil uil-lock-alt"></i>
                                                                    {touched.password && errors.password && (
                                                                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1, fontSize: '12px' }}>
                                                                            {errors.password}
                                                                        </Typography>
                                                                    )}
                                                                </div>
                                                                <button 
                                                                    type="submit"
                                                                    className="btn mt-4" 
                                                                    style={{ backgroundColor: '#ffeba7' }}
                                                                    disabled={isLoading}
                                                                >
                                                                    {isLoading ? "Logging In..." : "Login"}
                                                                </button>
                                                            </form>
                                                        )}
                                                    </Formik>
                                                    
                                                    <p className="mb-0 mt-4 text-center">
                                                        <a href="#" className="link">Forgot your password?</a>
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Sign Up Card Back */}
                                        <div className="card-back">
                                            <div className="center-wrap">
                                                <div className="section text-center">
                                                    <h4 className="mt-5 pt-3 mb-3 pb-3">Sign Up</h4>
                                                    <div className="form-group">
                                                        <input type="text" className="form-style" placeholder="Full Name" />
                                                        <i className="input-icon uil uil-user"></i>
                                                    </div>
                                                    <div className="form-group mt-2">
                                                        <input type="tel" className="form-style" placeholder="Phone Number" />
                                                        <i className="input-icon uil uil-phone"></i>
                                                    </div>
                                                    <div className="form-group mt-2">
                                                        <input type="email" className="form-style" placeholder="Email" />
                                                        <i className="input-icon uil uil-at"></i>
                                                    </div>
                                                    <button style={{ backgroundColor: '#ffeba7' }} className="btn mb-5 mt-3">
                                                        Register
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Box>
    );
}

export default LoginPage;